import { ResponsiveSankey } from '@nivo/sankey'
import { useState, useMemo } from 'react'
import { channelColor } from '../lib/channels'

// Color schemes for different visualization types
const colorSchemes = {
  flow: {
    // First touch (left) - blues
    first: '#3B82F6',
    // Last touch (middle) - greens
    last: '#10B981',
    // Conversion (right) - green for converted, gray for not
    converted: '#22C55E',
    not_converted: '#6B7280',
  },
  retargeting: {
    // Initial sources - blues
    initial: '#3B82F6',
    // Start aggregation node - slate
    start: '#64748B',
    // Retargeting sources - amber/orange
    retargeting: '#F59E0B',
    // Outcomes - green for converted
    converted: '#22C55E',
    not_converted: '#6B7280',
  },
  pharmacyCount: {
    clinic: '#3B82F6',    // blue
    pharmacy: '#F59E0B',  // amber
    status: '#10B981',    // green
  },
  // Link colors
  links: {
    toConverted: '#22C55E', // Green for conversion paths
    toNotConverted: '#9CA3AF', // Gray for non-conversion
    default: '#94A3B8', // Slate for middle links
  },
  // Matched by substring of the node id; colours from the app-wide palette.
  sources: Object.fromEntries(['meta', 'google', 'everflow', 'katalys', 'reddit', 'tiktok', 'bing', 'email', 'sms', 'organic', 'direct', 'other']
    .map(channel => [channel, channelColor(channel)])),
}

// Get link color - green for links going to conversion
const getLinkColor = (link) => {
  // Handle both string IDs and object references (nivo transforms links after processing)
  const target = link.target
  const targetId = typeof target === 'string' ? target : target?.id
  const targetGroup = typeof target === 'object' ? target?.group : null
  const targetIdLower = (targetId || '').toLowerCase()

  // Check by group first (most reliable)
  if (targetGroup === 'not_converted') {
    return colorSchemes.links.toNotConverted
  }
  if (targetGroup === 'converted') {
    return colorSchemes.links.toConverted
  }

  // Fallback to ID matching
  if (targetIdLower.includes('not converted') || targetIdLower.includes('not_converted')) {
    return colorSchemes.links.toNotConverted
  }
  if (targetIdLower.includes('converted')) {
    return colorSchemes.links.toConverted
  }

  return colorSchemes.links.default
}

// Get node color based on group or source
const getNodeColor = (node, type = 'flow') => {
  // Check for conversion status first
  if (node.id === 'converted' || node.group === 'converted') {
    return colorSchemes.flow.converted
  }
  if (node.id === 'not_converted' || node.group === 'not_converted') {
    return colorSchemes.flow.not_converted
  }

  // For pharmacyCount diagrams, use group-based coloring
  if (type === 'pharmacyCount' && node.group) {
    return colorSchemes.pharmacyCount[node.group] || '#6B7280'
  }

  // For flow diagrams, use group-based coloring
  if (type === 'flow' && node.group) {
    return colorSchemes.flow[node.group] || '#6B7280'
  }

  // For retargeting, use group-based coloring (initial, start, retargeting)
  if (type === 'retargeting' && node.group) {
    return colorSchemes.retargeting[node.group] || '#6B7280'
  }

  // Legacy: for retargeting, use position-based coloring
  if (type === 'retargeting' && node.position) {
    return colorSchemes.retargeting[node.position] || '#6B7280'
  }

  // Try to match by source name in node id
  const nodeIdLower = node.id.toLowerCase()
  for (const [source, color] of Object.entries(colorSchemes.sources)) {
    if (nodeIdLower.includes(source)) {
      return color
    }
  }

  return '#6B7280'
}

/**
 * Reusable Sankey diagram component for attribution visualizations
 *
 * @param {Object} props
 * @param {Object} props.data - Sankey data with nodes and links arrays
 * @param {string} props.type - 'flow' or 'retargeting' for color scheme
 * @param {number} props.height - Chart height in pixels
 * @param {boolean} props.loading - Show loading state
 * @param {string} props.emptyMessage - Message when no data
 */
export default function SankeyDiagram({
  data,
  type = 'flow',
  height = 500,
  loading = false,
  emptyMessage = 'No data available for the selected filters',
  layout = 'horizontal',
}) {
  const [tooltip, setTooltip] = useState(null)

  // Preprocess data to add link colors based on target node
  const processedData = useMemo(() => {
    if (!data?.nodes || !data?.links) return data

    // Create a map of node IDs to their groups
    const nodeGroups = {}
    data.nodes.forEach(node => {
      nodeGroups[node.id] = node.group
    })

    // Add color to each link based on target
    const linksWithColors = data.links.map(link => {
      const targetId = link.target
      const targetIdLower = (targetId || '').toLowerCase()
      const targetGroup = nodeGroups[targetId]

      let color = colorSchemes.links.default

      // Check by group first
      if (targetGroup === 'not_converted') {
        color = colorSchemes.links.toNotConverted
      } else if (targetGroup === 'converted') {
        color = colorSchemes.links.toConverted
      }
      // Fallback to ID matching
      else if (targetIdLower.includes('not converted') || targetIdLower.includes('not_converted')) {
        color = colorSchemes.links.toNotConverted
      } else if (targetIdLower.includes('converted')) {
        color = colorSchemes.links.toConverted
      }

      return { ...link, color }
    })

    return { ...data, links: linksWithColors }
  }, [data])

  // Loading state
  if (loading) {
    return (
      <div
        className="flex items-center justify-center bg-gray-50 dark:bg-gray-800 rounded-lg"
        style={{ height }}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-500 dark:text-gray-400">Loading attribution data...</p>
        </div>
      </div>
    )
  }

  // Empty state
  if (!processedData || !processedData.nodes || processedData.nodes.length === 0 || !processedData.links || processedData.links.length === 0) {
    return (
      <div
        className="flex items-center justify-center bg-gray-50 dark:bg-gray-800 rounded-lg"
        style={{ height }}
      >
        <div className="text-center text-gray-500 dark:text-gray-400">
          <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <p>{emptyMessage}</p>
        </div>
      </div>
    )
  }

  // Custom node tooltip with conversion data
  const NodeTooltip = ({ node }) => {
    const isConverted = node.group === 'converted' || node.id?.includes('Converted')
    const isNotConverted = node.group === 'not_converted' || node.id?.includes('Not Converted')
    const isEndNode = isConverted || isNotConverted

    // Calculate conversion stats for this node
    const incomingLinks = processedData.links?.filter(l =>
      (typeof l.target === 'string' ? l.target : l.target?.id) === node.id
    ) || []
    const totalIncoming = incomingLinks.reduce((sum, l) => sum + (l.value || 0), 0)
    const convertedIncoming = incomingLinks.reduce((sum, l) => sum + (l.converted_count || 0), 0)
    const cvr = totalIncoming > 0 ? ((convertedIncoming / totalIncoming) * 100).toFixed(1) : null

    return (
      <div className="bg-white dark:bg-gray-800 px-3 py-2 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 min-w-48">
        <div className="font-medium text-gray-900 dark:text-white mb-1">{node.label || node.id}</div>
        <div className="text-sm text-gray-500 dark:text-gray-400 space-y-0.5">
          <div>{node.value?.toLocaleString()} journeys</div>
          {node.group && !isEndNode && (
            <div className="capitalize text-xs text-gray-400">Stage: {node.group}</div>
          )}
          {cvr !== null && !isEndNode && (
            <div className="text-green-600 dark:text-green-400 font-medium">
              {cvr}% conversion rate
            </div>
          )}
        </div>
      </div>
    )
  }

  // Custom link tooltip with conversion details
  const LinkTooltip = ({ link }) => {
    const sourceLabel = link.source.label || link.source.id || link.source
    const targetLabel = link.target.label || link.target.id || link.target
    const isToConverted = targetLabel?.includes('Converted') && !targetLabel?.includes('Not')
    const hasConvertedCount = link.converted_count !== undefined && link.converted_count > 0
    const cvr = link.value > 0 && hasConvertedCount
      ? ((link.converted_count / link.value) * 100).toFixed(1)
      : null

    return (
      <div className="bg-white dark:bg-gray-800 px-3 py-2 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 min-w-52">
        <div className="font-medium text-gray-900 dark:text-white mb-1">
          {sourceLabel} → {targetLabel}
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400 space-y-0.5">
          <div>{link.value?.toLocaleString()} journeys</div>
          {hasConvertedCount && (
            <div className="text-green-600 dark:text-green-400">
              {link.converted_count?.toLocaleString()} converted ({cvr}%)
            </div>
          )}
          {isToConverted && (
            <div className="text-purple-600 dark:text-purple-400 text-xs">
              ✓ Conversion path
            </div>
          )}
        </div>
      </div>
    )
  }

  // Detect dark mode (check both Tailwind class and system preference)
  const isDarkMode = typeof window !== 'undefined' && (
    document.documentElement.classList.contains('dark') ||
    window.matchMedia('(prefers-color-scheme: dark)').matches
  )

  return (
    <div style={{ height }} className="bg-background rounded-lg">
      <ResponsiveSankey
        data={processedData}
        layout={layout}
        margin={
          layout === 'vertical'
            ? { top: 40, right: 40, bottom: 40, left: 40 }
            : { top: 20, right: 160, bottom: 20, left: 160 }
        }
        align="justify"
        colors={(node) => getNodeColor(node, type)}
        nodeOpacity={1}
        nodeHoverOpacity={1}
        nodeHoverOthersOpacity={0.35}
        nodeThickness={18}
        nodeSpacing={24}
        nodeBorderWidth={0}
        nodeBorderRadius={3}
        linkOpacity={0.6}
        linkHoverOpacity={0.85}
        linkHoverOthersOpacity={0.15}
        linkContract={3}
        linkColor={(link) => link.color || getLinkColor(link)}
        enableLinkGradient={false}
        labelPosition="outside"
        labelOrientation="horizontal"
        labelPadding={16}
        labelTextColor="#111827"
        nodeTooltip={NodeTooltip}
        linkTooltip={LinkTooltip}
        animate={true}
        motionConfig="gentle"
        theme={{
          background: 'transparent',
          labels: {
            text: {
              fill: '#111827',
              fontSize: 13,
              fontWeight: 600,
            }
          },
          tooltip: {
            container: {
              background: isDarkMode ? '#1F2937' : '#FFFFFF',
              color: isDarkMode ? '#F9FAFB' : '#111827',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            }
          }
        }}
      />
    </div>
  )
}

/**
 * Summary stats component to display alongside Sankey
 */
export function SankeySummary({ summary, className = '' }) {
  if (!summary) return null

  // Helper to format numeric values (handles strings from backend)
  const formatNumber = (val, decimals = 1) => {
    const num = typeof val === 'string' ? parseFloat(val) : val
    return isNaN(num) ? '0' : num.toFixed(decimals)
  }

  const stats = [
    {
      label: 'Total Journeys',
      value: (summary.total_journeys || summary.totalJourneys || 0).toLocaleString(),
      icon: '🛤️'
    },
    {
      label: 'Converted',
      value: (summary.converted || summary.total_converted || 0).toLocaleString(),
      icon: '✅'
    },
    {
      label: 'CVR',
      value: `${formatNumber(summary.cvr || summary.overallCvr || 0)}%`,
      icon: '📊',
      highlight: true
    },
  ]

  // Add multi-touch specific stats
  if (summary.avg_touches || summary.avgTouches) {
    stats.push({
      label: 'Avg Touches',
      value: formatNumber(summary.avg_touches || summary.avgTouches || 0),
      icon: '👆'
    })
  }

  if (summary.multi_touch_journeys || summary.multiTouchJourneys) {
    stats.push({
      label: 'Multi-Touch',
      value: (summary.multi_touch_journeys || summary.multiTouchJourneys || 0).toLocaleString(),
      icon: '🔄'
    })
  }

  return (
    <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 ${className}`}>
      {stats.map((stat, idx) => (
        <div
          key={idx}
          className={`
            p-4 rounded-lg border transition-colors
            ${stat.highlight
              ? 'bg-primary/10 border-primary/30'
              : 'bg-muted/50 border-border'
            }
          `}
        >
          <div className="text-2xl mb-1">{stat.icon}</div>
          <div className="text-2xl font-bold text-foreground">
            {stat.value}
          </div>
          <div className="text-sm text-muted-foreground">
            {stat.label}
          </div>
        </div>
      ))}
    </div>
  )
}
